import breakpoints from 'src/js/modules/element-settings/constants/breakpoints';
import { targetClasses } from 'src/js/modules/element-settings/constants/target-classes';
import paddingDefaults from '../constants/padding-defaults';
import { autoPaddingTargets } from '../constants/targets';
import {
  hasAutoPaddingTrigger,
  hasEnabledResolvedPadding,
  hasPaddingAtBreakpoint,
  hasResolvedPadding,
} from '../verifiers/needs-auto-padding';

const AUTO_PADDING_OFF_VALUE = '0px';

const paddingTargetByAutoPaddingTarget = {
  [targetClasses.AUTO_PADDING_TOP]: targetClasses.PADDING_TOP,
  [targetClasses.AUTO_PADDING_BOTTOM]: targetClasses.PADDING_BOTTOM,
  [targetClasses.AUTO_COL_PADDING_LEFT]: targetClasses.COL_PADDING_LEFT,
  [targetClasses.AUTO_COL_PADDING_RIGHT]: targetClasses.COL_PADDING_RIGHT,
};

const getDefaultAutoPaddingValue = (autoPaddingTarget, breakpointKey) => {
  const paddingTarget = paddingTargetByAutoPaddingTarget[autoPaddingTarget];

  return paddingDefaults[breakpointKey][paddingTarget];
};

const isAutoPaddingNeeded = ({ state, elementIds, autoPaddingTarget, breakpointKey }) => {
  const paddingTarget = paddingTargetByAutoPaddingTarget[autoPaddingTarget];

  return (
    hasAutoPaddingTrigger(state, elementIds, breakpointKey) &&
    !hasResolvedPadding(state, elementIds, paddingTarget, breakpointKey)
  );
};

const getExistingPositiveAutoPaddingValue = ({
  state,
  elementIds,
  autoPaddingTarget,
  breakpointKey,
}) => {
  const elementId = elementIds.find(
    (id) => parseFloat(state[id]?.[autoPaddingTarget]?.[breakpointKey]) > 0
  );

  return elementId ? state[elementId][autoPaddingTarget][breakpointKey] : undefined;
};

const shouldKeepAutoPaddingForInheritedPadding = ({
  state,
  elementIds,
  autoPaddingTarget,
  breakpointKey,
}) => {
  const paddingTarget = paddingTargetByAutoPaddingTarget[autoPaddingTarget];

  return (
    !hasPaddingAtBreakpoint(state, elementIds, paddingTarget, breakpointKey) &&
    hasEnabledResolvedPadding(state, elementIds, paddingTarget, breakpointKey) &&
    Boolean(
      getExistingPositiveAutoPaddingValue({
        state,
        elementIds,
        autoPaddingTarget,
        breakpointKey,
      })
    )
  );
};

const getAutoPaddingValueToApply = ({
  state,
  elementIds,
  autoPaddingTarget,
  breakpointKey,
  previousBreakpointKey,
}) => {
  if (isAutoPaddingNeeded({ state, elementIds, autoPaddingTarget, breakpointKey })) {
    return getDefaultAutoPaddingValue(autoPaddingTarget, breakpointKey);
  }

  if (
    shouldKeepAutoPaddingForInheritedPadding({
      state,
      elementIds,
      autoPaddingTarget,
      breakpointKey,
    })
  ) {
    return getExistingPositiveAutoPaddingValue({
      state,
      elementIds,
      autoPaddingTarget,
      breakpointKey,
    });
  }

  const wasNeededAtPreviousBreakpoint =
    previousBreakpointKey &&
    isAutoPaddingNeeded({
      state,
      elementIds,
      autoPaddingTarget,
      breakpointKey: previousBreakpointKey,
    });

  return wasNeededAtPreviousBreakpoint ? AUTO_PADDING_OFF_VALUE : undefined;
};

const getAutoPaddingValuesToApply = (state, elementIds) => {
  const valuesByBreakpoint = Object.fromEntries(breakpoints.map(({ key }) => [key, {}]));

  autoPaddingTargets.forEach((autoPaddingTarget) => {
    breakpoints.forEach(({ key: breakpointKey }, breakpointIndex) => {
      const previousBreakpointKey = breakpoints[breakpointIndex - 1]?.key;

      valuesByBreakpoint[breakpointKey][autoPaddingTarget] = getAutoPaddingValueToApply({
        state,
        elementIds,
        autoPaddingTarget,
        breakpointKey,
        previousBreakpointKey,
      });
    });
  });

  return valuesByBreakpoint;
};

const getAutoPaddingTargetBreakpointValues = ({
  state,
  elementId,
  autoPaddingTarget,
  valuesByBreakpoint,
}) => {
  const breakpointValues = {};

  breakpoints.forEach(({ key: breakpointKey }) => {
    const valueToApply = valuesByBreakpoint[breakpointKey][autoPaddingTarget];
    const currentValue = state[elementId]?.[autoPaddingTarget]?.[breakpointKey];

    if (valueToApply !== currentValue) {
      breakpointValues[breakpointKey] = valueToApply;
    }
  });

  return breakpointValues;
};

const getAutoPaddingElementTargets = ({ state, elementId, valuesByBreakpoint }) => {
  const targets = {};

  autoPaddingTargets.forEach((autoPaddingTarget) => {
    const breakpointValues = getAutoPaddingTargetBreakpointValues({
      state,
      elementId,
      autoPaddingTarget,
      valuesByBreakpoint,
    });

    if (Object.keys(breakpointValues).length) {
      targets[autoPaddingTarget] = breakpointValues;
    }
  });

  return targets;
};

const getAutoPaddingBreakpointBatch = ({ state, elementIds }) => {
  const batch = {};

  elementIds.forEach((elementId) => {
    const valuesByBreakpoint = getAutoPaddingValuesToApply(state, [elementId]);
    const targets = getAutoPaddingElementTargets({
      state,
      elementId,
      valuesByBreakpoint,
    });

    if (Object.keys(targets).length) {
      batch[elementId] = targets;
    }
  });

  return batch;
};

export default getAutoPaddingBreakpointBatch;
